import type {
  LibraryQuestion, QuestionCategory, SaveLibraryQuestion, SaveQuestionCategory,
  SaveVisitType, TokenPair, VisitType,
} from './types';

/**
 * Where the session lives (ADR-020).
 *
 * <p>The refresh token is in an HttpOnly cookie this code cannot read: injected script can
 * no longer carry a month of sessions away. It used to sit in localStorage beside the access
 * token, and GitHub's code scanning flagged it, rightly (4 November).</p>
 *
 * <p>The access token is held in memory only, and is gone when the page closes; the next
 * request fetches a fresh one with the cookie. What survives the page is the token's claims
 * without its signature — who is signed in and what they may do — so the screens, the menu
 * and the offline audit flow still open in a stockroom with no signal. Without the signature
 * those claims open nothing on the server.</p>
 *
 * <p>The store app in Phase 5 keeps the body flow and the device's secure store (ADR-002).</p>
 */
const PROFILE_KEY = 'denetim.profile';

/** Left by builds before the cookie; read once to move the session over, then removed. */
const LEGACY_ACCESS_KEY = 'denetim.access';
const LEGACY_REFRESH_KEY = 'denetim.refresh';

/** Asks the server to keep the refresh token in the cookie rather than the body. */
const COOKIE_MODE = { 'X-Norva-Refresh': 'cookie' } as const;

let memory: TokenPair | null = null;

/** The claims part of a JWT with its signature cut off: readable, and useless as a credential. */
export function unsigned(token: string): string {
  const [header, payload] = token.split('.');
  return header && payload ? `${header}.${payload}.` : '';
}

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * The session as the screens see it: the live pair while the page has one, otherwise the
 * remembered claims with no access token behind them yet.
 */
export function storedTokens(): TokenPair | null {
  if (memory) return memory;
  const profile = read(PROFILE_KEY) ?? (read(LEGACY_ACCESS_KEY) ? legacyProfile() : null);
  return profile
    ? { accessToken: profile, accessTokenExpiresAt: '', refreshToken: '', refreshTokenExpiresAt: '' }
    : null;
}

function legacyProfile(): string | null {
  try {
    const old = JSON.parse(read(LEGACY_ACCESS_KEY) ?? '') as TokenPair;
    return unsigned(old.accessToken) || null;
  } catch {
    return null;
  }
}

function store(tokens: TokenPair | null) {
  memory = tokens;
  try {
    localStorage.removeItem(LEGACY_ACCESS_KEY);
    localStorage.removeItem(LEGACY_REFRESH_KEY);
    if (tokens) localStorage.setItem(PROFILE_KEY, unsigned(tokens.accessToken));
    else localStorage.removeItem(PROFILE_KEY);
  } catch {
    // A private window with storage blocked still signs in; it just forgets on reload.
  }
}

/**
 * A refusal the server has explained.
 *
 * <p>The code is the part that matters: the screen looks it up in its own language. The
 * message travels with it in English so a build that has never heard of the code still has
 * something true to show — a newer server and an app that hasn't updated yet is a normal
 * pair during a rollout, not an error state.</p>
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code: string | null = null,
    readonly args: Record<string, string> = {},
  ) {
    super(message);
  }

  /** The caller's own mistake rather than a fault: worth showing them, not logging. */
  get isUserFixable() {
    return this.status === 400 || this.status === 409;
  }
}

let refreshing: Promise<TokenPair | null> | null = null;

/**
 * One refresh at a time — in this tab, and across tabs.
 *
 * <p>In this tab: a screen that fires four requests at once would otherwise send four
 * refreshes, and the server revokes the whole family when it sees a token used twice.</p>
 *
 * <p>Across tabs: two tabs whose access tokens ran out together used to refresh with the same
 * token, the second counted as a replay, and both were signed out (ADR-020). The Web Locks API
 * queues them; the second then refreshes with the cookie the first just rotated.</p>
 */
async function refreshTokens(): Promise<TokenPair | null> {
  refreshing ??= withRefreshLock(async () => {
    const response = await fetch('/api/v1/auth/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...COOKIE_MODE },
      // Empty unless this browser still holds a token from before the cookie; the server
      // moves that one into the cookie on this call.
      body: JSON.stringify({ refreshToken: read(LEGACY_REFRESH_KEY) ?? '' }),
    });

    if (!response.ok) {
      store(null);
      return null;
    }

    const tokens = (await response.json()) as TokenPair;
    store(tokens);
    return tokens;
  }).finally(() => {
    refreshing = null;
  });

  return refreshing;
}

function withRefreshLock<T>(work: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  return locks ? locks.request('norva-refresh', work) : work();
}

/**
 * One request, with the token attached and a single refresh on a 401.
 *
 * <p>Exported so other modules can build their own endpoint groups on the same plumbing
 * rather than each growing a private copy of the refresh logic — the family-revocation rule
 * means a second, naive implementation would sign people out.</p>
 */
async function send(path: string, init: RequestInit, accept: string, retry: boolean): Promise<Response> {
  // A page that has just opened has the remembered claims but no access token: get one
  // with the cookie before asking, rather than sending a request bound to fail.
  if (!memory && retry && storedTokens()) await refreshTokens();

  const tokens = memory;
  const headers = new Headers(init.headers);
  headers.set('Accept', accept);
  // Not for a FormData body: the browser sets multipart/form-data with the boundary it
  // generated, and naming the type ourselves produces a body the server cannot parse.
  if (init.body && !(init.body instanceof FormData)) headers.set('Content-Type', 'application/json');
  if (tokens) headers.set('Authorization', `Bearer ${tokens.accessToken}`);

  const response = await fetch(path, { ...init, headers });

  if (response.status === 401 && retry) {
    const fresh = await refreshTokens();
    if (fresh) return send(path, init, accept, false);
    throw new ApiError(401, 'Your session has ended.', 'session.expired');
  }

  if (!response.ok) {
    let message = `Unexpected error (${response.status}).`;
    let code: string | null = null;
    let args: Record<string, string> = {};

    try {
      const body = (await response.json()) as {
        code?: string;
        message?: string;
        args?: Record<string, string>;
      };
      if (body.message) message = body.message;
      if (body.code) code = body.code;
      if (body.args) args = body.args;
    } catch {
      // A body that is not JSON tells us nothing the status has not already said.
    }

    throw new ApiError(response.status, message, code, args);
  }

  return response;
}

export async function request<T>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
  const response = await send(path, init, 'application/json', retry);

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/**
 * Fetches a file the server generated, with the session attached.
 *
 * <p>Built on the same plumbing as every other call rather than beside it. A plain link
 * cannot carry the token, and a second copy of the refresh logic would sign people out —
 * the refresh family is revoked when an old token is replayed, so two implementations racing
 * each other end the session.</p>
 */
export async function download(path: string): Promise<{ blob: Blob; fileName: string | null }> {
  const response = await send(path, {}, '*/*', true);

  // The server names the file; the browser only ever sees a blob URL, which has no name of
  // its own.
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = /filename*?=(?:UTF-8'')?"?([^";]+)/i.exec(disposition);

  return {
    blob: await response.blob(),
    fileName: match ? decodeURIComponent(match[1]!) : null,
  };
}

/**
 * Posts a body and gets a file back, with the session attached — {@link download} for a GET that
 * needs a request body (an export whose shape is too large for the query string).
 */
export async function downloadPost(path: string, body: unknown): Promise<{ blob: Blob; fileName: string | null }> {
  const response = await send(path, { method: 'POST', body: JSON.stringify(body) }, '*/*', true);
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = /filename*?=(?:UTF-8'')?"?([^";]+)/i.exec(disposition);
  return {
    blob: await response.blob(),
    fileName: match ? decodeURIComponent(match[1]!) : null,
  };
}

const json = (method: string, body: unknown): RequestInit => ({
  method,
  body: JSON.stringify(body),
});

export const api = {
  async signIn(email: string, password: string): Promise<TokenPair> {
    const response = await fetch('/api/v1/auth/sign-in', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...COOKIE_MODE },
      body: JSON.stringify({ email, password }),
    });

    // Only a refusal is a refusal. A 502 from a proxy with nothing behind it, or a server
    // that is still starting, is not somebody mistyping their password — and telling them
    // it is sends them off to retype a password that was right all along.
    if (response.status === 401 || response.status === 400) {
      throw new ApiError(response.status, 'Email or password is wrong.', 'login.failed');
    }

    if (!response.ok) {
      throw new ApiError(response.status, `Sign-in failed: HTTP ${response.status}.`, 'login.unreachable');
    }

    const tokens = (await response.json()) as TokenPair;
    store(tokens);
    return tokens;
  },

  async signOut(): Promise<void> {
    // Always sent: the cookie may be there even when nothing else is, and only the server
    // can clear it.
    await fetch('/api/v1/auth/sign-out', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...COOKIE_MODE },
      body: JSON.stringify({ refreshToken: read(LEGACY_REFRESH_KEY) ?? '' }),
    }).catch(() => {
      // Signing out locally matters more than telling the server about it.
    });
    store(null);
  },

  tenant: () => request<{ id: string; code: string; name: string; region: string | null }>('/api/v1/tenant/me'),

  categories: {
    list: () => request<QuestionCategory[]>('/api/v1/library/categories'),
    create: (body: SaveQuestionCategory) =>
      request<QuestionCategory>('/api/v1/library/categories', json('POST', body)),
    update: (id: string, body: SaveQuestionCategory) =>
      request<QuestionCategory>(`/api/v1/library/categories/${id}`, json('PUT', body)),
    remove: (id: string) =>
      request<void>(`/api/v1/library/categories/${id}`, { method: 'DELETE' }),
    nextQuestionCode: (id: string) =>
      request<{ code: string }>(`/api/v1/library/categories/${id}/next-question-code`),
  },

  questions: {
    list: (categoryId?: string, search?: string) => {
      const query = new URLSearchParams();
      if (categoryId) query.set('categoryId', categoryId);
      if (search) query.set('search', search);
      const suffix = query.toString();
      return request<LibraryQuestion[]>(`/api/v1/library/questions${suffix ? `?${suffix}` : ''}`);
    },
    create: (body: SaveLibraryQuestion) =>
      request<LibraryQuestion>('/api/v1/library/questions', json('POST', body)),
    update: (id: string, body: SaveLibraryQuestion) =>
      request<LibraryQuestion>(`/api/v1/library/questions/${id}`, json('PUT', body)),
    remove: (id: string) =>
      request<void>(`/api/v1/library/questions/${id}`, { method: 'DELETE' }),
    retire: (id: string) =>
      request<void>(`/api/v1/library/questions/${id}/retire`, { method: 'POST' }),
    reinstate: (id: string) =>
      request<void>(`/api/v1/library/questions/${id}/reinstate`, { method: 'POST' }),
  },

  visitTypes: {
    list: () => request<VisitType[]>('/api/v1/library/visit-types'),
    create: (body: SaveVisitType) =>
      request<VisitType>('/api/v1/library/visit-types', json('POST', body)),
    update: (id: string, body: SaveVisitType) =>
      request<VisitType>(`/api/v1/library/visit-types/${id}`, json('PUT', body)),
    remove: (id: string) =>
      request<void>(`/api/v1/library/visit-types/${id}`, { method: 'DELETE' }),
  },
};
